import { useEffect, useMemo, useState } from "react";
import {
  Alert, Avatar, Backdrop, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, MenuItem, Paper, Snackbar, Stack,
  TextField, Typography, useTheme,
} from "@mui/material";
import {
  AccountBalanceOutlined, ArrowBackOutlined, CloudUploadOutlined, DeleteOutline,
  DescriptionOutlined, DownloadOutlined, HomeOutlined, OpenInNewOutlined,
  PersonOutline, SaveOutlined, VisibilityOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConnString from "../../../ConnString.js";

const apiRoot = ConnString.connstring;
const initialForm = {
  FirstName: "", MidName: "", LastName: "", Alias: "", TINAll: "",
  Address1: "", Address2: "", ZipCode: "", CelNo: "", TelNo: "",
  Email: "", Branch: "",
};

const formatTin = (value) => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 13);
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9), digits.slice(9, 13)]
    .filter(Boolean)
    .join("-");
};

const readableSize = (size = 0) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const branchCode = (branch) => String(branch?.Branch ?? branch?.BranchCode ?? branch?.id ?? "");
const branchName = (branch) => String(branch?.LocName ?? branch?.BranchName ?? branchCode(branch));

const SectionTitle = ({ icon, title, subtitle, motif }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
    <Box sx={{ width: 40, height: 40, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 2.5, color: motif.main, bgcolor: motif.panel }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={800} color={motif.text}>{title}</Typography>
      <Typography variant="body2" color={motif.muted}>{subtitle}</Typography>
    </Box>
  </Stack>
);

const UploadCard = ({ title, description, accept, file, existingDocument, preview, onSelect, onRemove, onPreview, motif, photo = false, disabled = false }) => {
  const hasDocument = Boolean(file || existingDocument);
  const displayName = file?.name || existingDocument?.OriginalFileName || "";
  const displaySize = file?.size ?? existingDocument?.FileSize ?? 0;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, height: "100%", borderRadius: 3,
        borderColor: hasDocument ? motif.main : motif.border,
        borderStyle: "dashed", bgcolor: hasDocument ? motif.panel : motif.paper,
      }}
    >
      <Stack spacing={1.5} alignItems="center" textAlign="center">
        {photo ? (
          <Avatar src={preview || undefined} sx={{ width: 76, height: 76, bgcolor: motif.panelStrong, color: motif.main }}>
            <PersonOutline fontSize="large" />
          </Avatar>
        ) : (
          <Box sx={{ width: 60, height: 60, display: "grid", placeItems: "center", borderRadius: 3, bgcolor: motif.panelStrong, color: motif.main }}>
            <DescriptionOutlined fontSize="large" />
          </Box>
        )}
        <Box sx={{ minHeight: 54 }}>
          <Typography fontWeight={800} color={motif.text}>{title}</Typography>
          <Typography variant="caption" color={motif.muted}>{description}</Typography>
        </Box>
        {hasDocument && (
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap title={displayName}>{displayName}</Typography>
            <Typography variant="caption" color={motif.muted}>{readableSize(displaySize)}{existingDocument && !file ? " · Saved" : ""}</Typography>
          </Box>
        )}
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          {hasDocument && (
            <Button size="small" variant="contained" startIcon={<VisibilityOutlined />} onClick={onPreview}>Preview</Button>
          )}
          {!disabled && (
            <>
              <Button component="label" size="small" variant={hasDocument ? "outlined" : "contained"} startIcon={<CloudUploadOutlined />}>
                {hasDocument ? "Replace" : "Choose file"}
                <input hidden type="file" accept={accept} onChange={(event) => {
                  onSelect(event.target.files?.[0] || null);
                  event.target.value = "";
                }} />
              </Button>
              {file && (
                <Button size="small" color="error" variant="text" startIcon={<DeleteOutline />} onClick={onRemove}>Undo</Button>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const MFBorrowers = () => {
  const theme = useTheme();
  const motif = theme.palette.motif;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = String(params.get("mode") || "new").toLowerCase();
  const recID = params.get("recid") || "";
  const routeBranch = params.get("branch") || "";
  const editing = mode === "edit";
  const viewing = mode === "view";
  const compID = sessionStorage.getItem("compID") || "";
  const userID = sessionStorage.getItem("userID") || "";
  const [form, setForm] = useState(initialForm);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [status, setStatus] = useState("Active");
  const [files, setFiles] = useState({ borrowerPhoto: null, validId: null, clientInformationSheet: null });
  const [existingDocuments, setExistingDocuments] = useState({});
  const [documentPreview, setDocumentPreview] = useState({
    open: false, loading: false, title: "", fileName: "", mimeType: "", url: "", error: "",
  });

  const photoPreview = useMemo(() => (files.borrowerPhoto ? URL.createObjectURL(files.borrowerPhoto) : ""), [files.borrowerPhoto]);
  const homeAddressLength = useMemo(
    () => [form.Address1.trim(), form.Address2.trim()].filter(Boolean).join(" ").length,
    [form.Address1, form.Address2],
  );

  useEffect(() => () => { if (photoPreview) URL.revokeObjectURL(photoPreview); }, [photoPreview]);
  useEffect(() => () => { if (documentPreview.url) URL.revokeObjectURL(documentPreview.url); }, [documentPreview.url]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const branchResponse = await fetch(`${apiRoot}/api/userBranchAccess`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companyID: compID, currentuserID: userID }),
        });
        const branchData = await branchResponse.json();
        if (!branchResponse.ok || branchData.success === false) throw new Error(branchData.message || "Unable to load your branches.");
        if (!active) return;
        const options = branchData.options || [];
        const selected = options.find((item) => branchCode(item) === routeBranch)
          || options.find((item) => Boolean(item.DefaultBranch)) || options[0];
        setBranches(options);

        if (editing || viewing) {
          if (!recID) throw new Error("No borrower record was selected.");
          const loadResponse = await fetch(`${apiRoot}/api/lending/borrowers/load`, {
            method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ compID, userID, recID, mode }),
          });
          const loadData = await loadResponse.json();
          if (!loadResponse.ok || loadData.success === false) throw new Error(loadData.message || "Unable to load the borrower.");
          if (!active) return;
          setForm({ ...initialForm, ...loadData.borrower });
          setStatus(loadData.borrower?.Status || (loadData.borrower?.Active_ ? "Active" : "Inactive"));
          const documentFields = {
            BORROWER_PHOTO: "borrowerPhoto",
            VALID_ID: "validId",
            CLIENT_INFORMATION_SHEET: "clientInformationSheet",
          };
          setExistingDocuments((loadData.documents || []).reduce((result, document) => {
            const field = documentFields[document.DocumentType];
            if (field && !result[field]) result[field] = document;
            return result;
          }, {}));
        } else {
          setForm((current) => ({ ...current, Branch: branchCode(selected) }));
        }
      } catch (error) {
        if (active) setNotice({ severity: "error", text: error.message });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [compID, editing, mode, recID, routeBranch, userID, viewing]);

  const fieldSx = viewing ? {} : {
    "& .MuiOutlinedInput-root": { bgcolor: theme.palette.mode === "dark" ? "rgba(255,193,7,.10)" : "#fff8dc" },
  };
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateFile = (field, file) => setFiles((current) => ({ ...current, [field]: file }));
  const selectFile = (field, file, allowedTypes, maxSize, label) => {
    if (!file) return;
    if (!allowedTypes.includes(file.type)) {
      setNotice({ severity: "warning", text: `${label} uses an unsupported file format.` });
      return;
    }
    if (file.size > maxSize) {
      setNotice({ severity: "warning", text: `${label} exceeds the allowed ${readableSize(maxSize)} file size.` });
      return;
    }
    updateFile(field, file);
  };

  const closeDocumentPreview = () => {
    if (documentPreview.url) URL.revokeObjectURL(documentPreview.url);
    setDocumentPreview({ open: false, loading: false, title: "", fileName: "", mimeType: "", url: "", error: "" });
  };

  const previewDocument = async (title, file, existingDocument) => {
    setDocumentPreview({ open: true, loading: true, title, fileName: file?.name || existingDocument?.OriginalFileName || "Document", mimeType: file?.type || existingDocument?.MimeType || "", url: "", error: "" });
    try {
      let blob = file;
      let mimeType = file?.type || "";
      if (!blob) {
        if (!existingDocument?.DocumentID) throw new Error("No uploaded document is available to preview.");
        const response = await fetch(`${apiRoot}/api/lending/borrowers/documents/preview`, {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ compID, userID, documentID: existingDocument.DocumentID }),
        });
        if (!response.ok) {
          const raw = await response.text();
          let data;
          try { data = JSON.parse(raw); } catch { data = {}; }
          throw new Error(data.message || "Unable to load the borrower document.");
        }
        blob = await response.blob();
        mimeType = blob.type || existingDocument.MimeType || "application/octet-stream";
      }
      const url = URL.createObjectURL(blob);
      setDocumentPreview({
        open: true, loading: false, title,
        fileName: file?.name || existingDocument?.OriginalFileName || "Document",
        mimeType, url, error: "",
      });
    } catch (error) {
      setDocumentPreview((current) => ({ ...current, loading: false, error: error.message }));
    }
  };

  const validate = () => {
    if (!form.FirstName.trim() || !form.LastName.trim()) return "First name and last name are required.";
    if (!form.Address1.trim() || !form.Address2.trim()) return "Complete home address is required.";
    if (homeAddressLength > 100) return "The combined home address must not exceed 100 characters.";
    if (!form.CelNo.trim()) return "Mobile number is required.";
    if (!form.Branch) return "Select the borrower's branch.";
    if (form.TINAll && !/^\d{3}-\d{3}-\d{3}-\d{4}$/.test(form.TINAll)) return "Complete the TIN using 000-000-000-0000, or leave it blank.";
    if (form.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.Email)) return "Enter a valid email address.";
    return "";
  };

  const save = async () => {
    const validationMessage = validate();
    if (validationMessage) {
      setNotice({ severity: "warning", text: validationMessage });
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("compID", compID);
      payload.append("userID", userID);
      if (editing) payload.append("recID", recID);
      payload.append("borrower", JSON.stringify(form));
      Object.entries(files).forEach(([field, file]) => { if (file) payload.append(field, file); });
      const response = await fetch(`${apiRoot}/api/lending/borrowers/${editing ? "update" : "create"}`, {
        method: "POST", credentials: "include", body: payload,
      });
      const raw = await response.text();
      let data;
      try { data = JSON.parse(raw); } catch { throw new Error("The server returned an invalid response."); }
      if (!response.ok || data.success === false) throw new Error(data.message || `Unable to ${editing ? "update" : "create"} the borrower.`);
      setNotice({ severity: "success", text: data.message || `Borrower ${editing ? "updated" : "created"} successfully.` });
      window.setTimeout(() => navigate("/App/BorrowerList"), 900);
    } catch (error) {
      setNotice({ severity: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, minHeight: "100%", bgcolor: motif.page }}>
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, bgcolor: motif.paper, border: `1px solid ${motif.border}`, borderTop: `4px solid ${motif.main}` }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 52, height: 52, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 3, bgcolor: motif.main, color: theme.palette.primary.contrastText }}>
                <PersonOutline />
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="h4" fontWeight={800} color={motif.text}>{viewing ? "View" : editing ? "Edit" : "New"} Borrower</Typography>
                  <Box sx={{ px: 1, py: 0.25, borderRadius: 5, color: motif.main, border: `1px solid ${motif.border}`, fontSize: 11, fontWeight: 800 }}>{String(status || "Active").toUpperCase()}</Box>
                </Stack>
                <Typography color={motif.muted}>{editing || viewing ? "Borrower profile and supporting documents." : "The borrower code will be generated automatically when saved."}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button startIcon={<ArrowBackOutlined />} color="inherit" onClick={() => navigate("/App/BorrowerList")}>Back</Button>
              {!viewing && (
                <Button variant="contained" startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveOutlined />} disabled={loading || saving} onClick={save} sx={{ minWidth: 120 }}>
                  {saving ? (editing ? "Updating…" : "Saving…") : (editing ? "Update" : "Save")}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={800} color={motif.text}>Borrower Profile</Typography>
              <Typography variant="body2" color={motif.muted}>Enter the borrower's identity and contact information.</Typography>
            </Box>
            <Typography variant="caption" color={motif.muted}>Yellow fields are editable · * Required</Typography>
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: motif.border, bgcolor: motif.paper }}>
              <SectionTitle icon={<PersonOutline />} title="Personal Identity" subtitle="Legal name and tax identity used in the Agent record." motif={motif} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                <TextField disabled={viewing} label="First Name *" value={form.FirstName} onChange={(event) => updateField("FirstName", event.target.value)} inputProps={{ maxLength: 100 }} sx={fieldSx} />
                <TextField disabled={viewing} label="Middle Name" value={form.MidName} onChange={(event) => updateField("MidName", event.target.value)} inputProps={{ maxLength: 100 }} sx={fieldSx} />
                <TextField disabled={viewing} label="Last Name *" value={form.LastName} onChange={(event) => updateField("LastName", event.target.value)} inputProps={{ maxLength: 100 }} sx={fieldSx} />
                <TextField disabled={viewing} label="Alias / Nickname" value={form.Alias} onChange={(event) => updateField("Alias", event.target.value)} inputProps={{ maxLength: 100 }} sx={fieldSx} />
                <TextField
                  disabled={viewing}
                  label="TIN"
                  value={form.TINAll}
                  onChange={(event) => updateField("TINAll", formatTin(event.target.value))}
                  placeholder="Leave blank to generate automatically"
                  helperText="If blank, the last four digits will come from the generated borrower code."
                  inputProps={{ inputMode: "numeric", maxLength: 16 }}
                  sx={{ ...fieldSx, gridColumn: { sm: "1 / -1" } }}
                />
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: motif.border, bgcolor: motif.paper }}>
              <SectionTitle icon={<HomeOutlined />} title="Address & Contact" subtitle="Current residence and primary communication details." motif={motif} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                <TextField disabled={viewing} label="House No., Street, Barangay *" value={form.Address1} onChange={(event) => updateField("Address1", event.target.value)} sx={{ ...fieldSx, gridColumn: { sm: "1 / -1" } }} />
                <TextField disabled={viewing} label="City / Municipality, Province *" value={form.Address2} onChange={(event) => updateField("Address2", event.target.value)} error={homeAddressLength > 100} helperText={`${homeAddressLength}/100 combined address characters`} sx={{ ...fieldSx, gridColumn: { sm: "1 / -1" } }} />
                <TextField disabled={viewing} label="ZIP Code" value={form.ZipCode} onChange={(event) => updateField("ZipCode", event.target.value.replace(/\D/g, "").slice(0, 10))} inputProps={{ inputMode: "numeric" }} sx={fieldSx} />
                <TextField disabled={viewing} label="Mobile Number *" value={form.CelNo} onChange={(event) => updateField("CelNo", event.target.value)} sx={fieldSx} />
                <TextField disabled={viewing} label="Telephone Number" value={form.TelNo} onChange={(event) => updateField("TelNo", event.target.value)} sx={fieldSx} />
                <TextField disabled={viewing} label="Email Address" type="email" value={form.Email} onChange={(event) => updateField("Email", event.target.value)} inputProps={{ maxLength: 100 }} sx={fieldSx} />
              </Box>
            </Paper>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: motif.border }} />
          <SectionTitle icon={<AccountBalanceOutlined />} title="Lending Profile" subtitle="Assign the branch responsible for maintaining this borrower." motif={motif} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 420px) 1fr" }, gap: 2, alignItems: "center" }}>
            <TextField select label="Assigned Branch *" value={form.Branch} onChange={(event) => updateField("Branch", event.target.value)} disabled={loading || viewing} sx={fieldSx}>
              {branches.map((branch) => <MenuItem key={branchCode(branch)} value={branchCode(branch)}>{branchCode(branch)} - {branchName(branch)}</MenuItem>)}
            </TextField>
            <Alert severity="info" variant="outlined" sx={{ bgcolor: motif.panel }}>
              {editing || viewing ? "Borrower profile information is synchronized with its Agent customer record." : "Saving creates one synchronized borrower profile and Agent customer record. The borrower starts as Active."}
            </Alert>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <SectionTitle icon={<CloudUploadOutlined />} title="Supporting Documents" subtitle="Attach available identity and application files to the borrower record." motif={motif} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
            <UploadCard disabled={viewing} title="Borrower Picture" description="JPG, PNG, or WEBP · Maximum 5 MB" accept="image/jpeg,image/png,image/webp" file={files.borrowerPhoto} existingDocument={existingDocuments.borrowerPhoto} preview={photoPreview} onSelect={(file) => selectFile("borrowerPhoto", file, ["image/jpeg", "image/png", "image/webp"], 5 * 1024 * 1024, "Borrower picture")} onRemove={() => updateFile("borrowerPhoto", null)} onPreview={() => previewDocument("Borrower Picture", files.borrowerPhoto, existingDocuments.borrowerPhoto)} motif={motif} photo />
            <UploadCard disabled={viewing} title="Valid ID" description="JPG, PNG, or PDF · Maximum 10 MB" accept="image/jpeg,image/png,application/pdf" file={files.validId} existingDocument={existingDocuments.validId} onSelect={(file) => selectFile("validId", file, ["image/jpeg", "image/png", "application/pdf"], 10 * 1024 * 1024, "Valid ID")} onRemove={() => updateFile("validId", null)} onPreview={() => previewDocument("Valid ID", files.validId, existingDocuments.validId)} motif={motif} />
            <UploadCard disabled={viewing} title="Client Information Sheet" description="PDF, Word, JPG, or PNG · Maximum 15 MB" accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png" file={files.clientInformationSheet} existingDocument={existingDocuments.clientInformationSheet} onSelect={(file) => selectFile("clientInformationSheet", file, ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/jpeg", "image/png"], 15 * 1024 * 1024, "Client information sheet")} onRemove={() => updateFile("clientInformationSheet", null)} onPreview={() => previewDocument("Client Information Sheet", files.clientInformationSheet, existingDocuments.clientInformationSheet)} motif={motif} />
          </Box>
        </Paper>
      </Stack>

      <Dialog open={documentPreview.open} onClose={closeDocumentPreview} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {documentPreview.title}
          <Typography variant="body2" color="text.secondary" noWrap>{documentPreview.fileName}</Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: { xs: 360, md: 620 }, p: 1.5, display: "grid", placeItems: "center", bgcolor: motif.page }}>
          {documentPreview.loading && <CircularProgress />}
          {!documentPreview.loading && documentPreview.error && <Alert severity="error">{documentPreview.error}</Alert>}
          {!documentPreview.loading && !documentPreview.error && documentPreview.url && documentPreview.mimeType.startsWith("image/") && (
            <Box component="img" src={documentPreview.url} alt={documentPreview.fileName} sx={{ display: "block", maxWidth: "100%", maxHeight: "72vh", objectFit: "contain" }} />
          )}
          {!documentPreview.loading && !documentPreview.error && documentPreview.url && documentPreview.mimeType === "application/pdf" && (
            <Box component="iframe" title={documentPreview.fileName} src={documentPreview.url} sx={{ width: "100%", height: { xs: 500, md: 650 }, border: 0, bgcolor: "white" }} />
          )}
          {!documentPreview.loading && !documentPreview.error && documentPreview.url && !documentPreview.mimeType.startsWith("image/") && documentPreview.mimeType !== "application/pdf" && (
            <Stack alignItems="center" spacing={2} textAlign="center">
              <DescriptionOutlined sx={{ fontSize: 72, color: motif.main }} />
              <Typography variant="h6" fontWeight={800}>{documentPreview.fileName}</Typography>
              <Typography color="text.secondary">This file type cannot be displayed directly by the browser. Use Open or Download to view it in its associated application.</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {documentPreview.url && (
            <>
              <Button startIcon={<OpenInNewOutlined />} onClick={() => window.open(documentPreview.url, "_blank")}>Open</Button>
              <Button component="a" href={documentPreview.url} download={documentPreview.fileName} startIcon={<DownloadOutlined />}>Download</Button>
            </>
          )}
          <Button onClick={closeDocumentPreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(notice)} autoHideDuration={notice?.severity === "success" ? 2400 : 6000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={notice?.severity || "info"} variant="filled" onClose={() => setNotice(null)} sx={{ minWidth: { xs: "90vw", sm: 440 }, boxShadow: 6 }}>{notice?.text}</Alert>
      </Snackbar>
      <Backdrop open={saving || loading} sx={{ zIndex: (muiTheme) => muiTheme.zIndex.modal + 1, color: "#fff", bgcolor: "rgba(0,0,0,.58)" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" fontWeight={800}>{loading ? "Loading borrower profile…" : editing ? "Updating borrower profile and documents…" : "Saving borrower profile and documents…"}</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
};

export default MFBorrowers;
